/**
 * Regra pura de "Pontos por fase" e das contagens de "Reconhecimentos" do Resumo do Meu perfil. Recebe os quatro
 * históricos que as abas já leem e as fases do ciclo; devolve o que a tela desenha. Datas em milissegundos.
 */
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { FaseDoCiclo, fasesMensaisDoCiclo, faseMensalDe } from 'pages/RealizacaoDestaque/realizacoes-do-semestre';

import { Comportamento } from './perfil-calculos';

/** Os quatro programas do gráfico, na ordem da legenda e do empilhamento (de baixo para cima). */
export const PROGRAMAS = ['reconhecimentos', 'realizacoes', 'ecoa', 'engajamento'] as const;
export type Programa = (typeof PROGRAMAS)[number];

/** Item de qualquer um dos históricos: só o instante do crédito e os pontos importam aqui. */
export interface ItemDoHistorico {
  time?: number;
  /** Reconhecimento cancelado vem com `total: null` do `find/extrato`. */
  total?: number | null;
}

/** Item do `find/extrato`: o comportamento e a moeda do reconhecimento (nulos no cancelado). */
export interface ItemDeReconhecimento {
  behavior?: string | null;
  coin?: string | null;
}

export type Historicos = Record<Programa, ItemDoHistorico[]> & { reconhecimentos: (ItemDoHistorico & ItemDeReconhecimento)[] };

export type SituacaoDaFase = 'encerrada' | 'em-andamento' | 'futura';

export interface ColunaDaFase {
  id: string;
  /** Nome do mês do início da fase ("Agosto"). */
  mes: string;
  situacao: SituacaoDaFase;
  /** "Encerrada", "Em andamento" ou "Abre em dd/MM". */
  rotuloDaSituacao: string;
  /** Soma dos quatro programas; ausente na fase que ainda não começou. */
  total?: number;
  pontos: Record<Programa, number>;
}

function situacaoDa(fase: FaseDoCiclo, agora: number): SituacaoDaFase {
  if (fase.date_start > agora) return 'futura';
  if (fase.date_finish < agora) return 'encerrada';

  return 'em-andamento';
}

function rotuloDa(situacao: SituacaoDaFase, fase: FaseDoCiclo): string {
  if (situacao === 'futura') return `Abre em ${format(new Date(fase.date_start), 'dd/MM')}`;

  return situacao === 'encerrada' ? 'Encerrada' : 'Em andamento';
}

function nomeDoMes(instante: number): string {
  const mes = format(new Date(instante), 'MMMM', { locale: ptBR });

  return mes.charAt(0).toUpperCase() + mes.slice(1);
}

const semPontos = (): Record<Programa, number> => ({ reconhecimentos: 0, realizacoes: 0, ecoa: 0, engajamento: 0 });

/**
 * AJ2-29 a AJ2-31: uma coluna por fase mensal do ciclo, em ordem de início. Cada item cai na fase cujo
 * `date_start..date_finish` contém o seu `time`; item sem `time` ou fora de toda fase não conta.
 */
export function colunasPorFase(historicos: Historicos, fases: FaseDoCiclo[] | undefined, agora: number): ColunaDaFase[] {
  const mensais = [...fasesMensaisDoCiclo(fases)].sort((a, b) => a.date_start - b.date_start);
  const pontosDaFase = new Map(mensais.map(fase => [fase._id, semPontos()]));

  PROGRAMAS.forEach(programa => {
    historicos[programa].forEach(item => {
      if (typeof item.time !== 'number') return;
      const fase = faseMensalDe(item.time, mensais);
      const pontos = fase && pontosDaFase.get(fase._id);
      if (pontos) pontos[programa] += item.total ?? 0;
    });
  });

  return mensais.map(fase => {
    const situacao = situacaoDa(fase, agora);
    const pontos = pontosDaFase.get(fase._id) ?? semPontos();
    const total = PROGRAMAS.reduce((soma, programa) => soma + pontos[programa], 0);

    return {
      id: fase._id,
      mes: nomeDoMes(fase.date_start),
      situacao,
      rotuloDaSituacao: rotuloDa(situacao, fase),
      total: situacao === 'futura' ? undefined : total,
      pontos,
    };
  });
}

/** Valores de `coin` do `find/extrato`, na ordem em que a tela os lista. */
export const MOEDAS = ['uau', 'mandou', 'valeu'] as const;
export type Moeda = (typeof MOEDAS)[number];

export const ROTULO_DA_MOEDA: Record<Moeda, string> = { uau: '#UAU', mandou: '#MANDOUBEM', valeu: '#VALEU' };

export type ContagemPorMoeda = Record<Moeda, number>;

const ehMoeda = (coin: unknown): coin is Moeda => MOEDAS.includes(coin as Moeda);

/**
 * AJ2-33: quantos reconhecimentos de cada moeda cada comportamento recebeu. Cancelados vêm sem `coin` e `behavior`
 * e ficam de fora, como qualquer moeda fora da lista.
 */
export function contagensPorCoin(itens: ItemDeReconhecimento[]): Partial<Record<Comportamento, ContagemPorMoeda>> {
  const contagens: Partial<Record<Comportamento, ContagemPorMoeda>> = {};

  itens.forEach(({ behavior, coin }) => {
    if (!behavior || !ehMoeda(coin)) return;
    const contagem = (contagens[behavior as Comportamento] ??= { uau: 0, mandou: 0, valeu: 0 });
    contagem[coin] += 1;
  });

  return contagens;
}
